import { Button, Link, MenuItem, Select, Stack, Typography } from "@mui/material";
import React from "react";
import { GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid-premium";
import { eventsNotifier, workflowService } from "../../../../Core/Api/services";
import { useToggle } from "../../../../Utils/Hooks/useToggle";
import AssetPanel from "../../../../Components/Panels/AssetPanel/asset-panel";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../../Router/router";
import { DbWorkflowInstanceInfos } from "../../../../Core/Api/Types/db-workflow-instance-infos";
import { WorkflowStatus } from "../../../../Core/Api/Types/workflow-status";
import TableCellAsset from "../../../../Components/Table/TableCellAsset/table-cell-asset";
import TableCellJobStatus from "../../../../Components/Table/TableCellJobStatus/table-cell-job-status";
import { EventNotifierOperation } from "../../../../Core/Api/notifier-client";
import { produce } from "immer";
import { WorkflowFamily } from "../../../../Core/Api/Types/workflow-family";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import SettingsApplicationsOutlinedIcon from "@mui/icons-material/SettingsApplicationsOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import TableCellMovie from "../../../../Components/Table/TableCellMovie/table-cell-movie";
import TableCellOwner2Lines from "../../../../Components/Table/TableCellOwner2Lines/table-cell-owner-2-lines";
import moment from "moment";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { AclMatch, UserHasAcls, GetRowValueFromStatus } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { JobStatus } from "../../../../Core/Api/Types/job-status";
import TableCellWorkflowProgressBar from "../../../../Components/Table/TableCellWorkflowProgressBar/table-cell-workflow-progress-bar";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function DashboardPurge() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = React.useState<string>("Default");
  const [typeFilter, setTypeFilter] = React.useState<string>("Default");
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [assetSelected, setAssetSelected] = React.useState<number>();
  const [rowSelected, setRowSelected] = React.useState<number>();
  const { active: assetPanel, toggle: toggleAssetPanelOpen } = useToggle(false);
  const [purge, setPurge] = React.useState<DbWorkflowInstanceInfos[]>([]);
  const [selectedPurge, setSelectedPurge] = React.useState<number[]>([]);

  const enqueueCustomSnackbar = useCustomSnackbar();

  const onClickRetryWorkflow = (id: number) => {
    workflowService.RetryInstance(id, true).then(function () {});
  };

  const onConfirmPurge = React.useCallback(
    (ids: number[]) => {
      let itemsToPurge: DbWorkflowInstanceInfos[] = purge.filter((x) => ids.includes(x.id));
      itemsToPurge.forEach((purge) => {
        if (
          purge.status === WorkflowStatus.ABORTED ||
          purge.status === WorkflowStatus.CANCELLED ||
          purge.status === WorkflowStatus.FAILED
        )
          onClickRetryWorkflow(purge.id);
        else if (purge.status !== WorkflowStatus.COMPLETED)
          workflowService.PlayInstance(purge.id).then((response) => {
            if (response.success) enqueueCustomSnackbar(t("alert:success.workflowPlayed", { id: purge.id }), "success");
            else
              enqueueCustomSnackbar(
                t("alert:warning.workflowWasNotPlayed", { id: purge.id, message: response.message }),
                "warning"
              );
          });
      });
    },
    [purge]
  );

  const handleSelection = React.useCallback((rowsSelected) => {
    setSelectedPurge(rowsSelected);
  }, []);

  React.useEffect(() => {
    workflowService.GetInstancesByFamilyAndDays(WorkflowFamily.PurgeMediaFile, 30).then(function (response) {
      if (response.success && response.instances)
        setPurge(
          response.instances.sort((a, b) => {
            if (a.creationInfos.date && b.creationInfos.date && a.creationInfos.date < b.creationInfos.date) return 1;
            else return -1;
          })
        );
      setIsLoading(false);
    });

    eventsNotifier.Connect().then(function () {
      if (eventsNotifier.connection) {
        eventsNotifier.connection.on("WorkflowInstanceMessage", (data) => {
          switch (data.op) {
            case EventNotifierOperation.Create:
              setPurge(
                produce((draft) => {
                  if (data.data.definitionFamily === WorkflowFamily.PurgeMediaFile) draft.unshift(data.data);
                })
              );
              break;
            case EventNotifierOperation.Update:
              setPurge(
                produce((draft) => {
                  const index = draft!.findIndex((x) => x.id === data.data.id)!;
                  if (index !== -1) draft[index] = data.data;
                })
              );
              break;
            case EventNotifierOperation.Delete:
              setPurge(
                produce((draft) => {
                  draft.filter((x) => x.id !== data.data.id);
                })
              );
              break;
          }
        });
      }
    });

    return function cleanUp() {
      eventsNotifier.Disconnect();
    };
  }, []);

  const purgeRows: GridRowsProp = purge;
  type Row = (typeof purgeRows)[number];

  const purgeColumns: GridColDef[] = [
    {
      field: "asset.parentTitleName",
      headerName: t("common:title"),
      valueGetter: (data: Row, row: any) =>
        !row ? "" : row["asset"].parentTitleName + " " + row["asset"].parentTitleId,
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <TableCellMovie id={data.row["asset"].parentTitleId} name={data.row["asset"].parentTitleName} />
      ),
    },
    {
      field: "creationInfos.date",
      headerName: t("common:upperCaseCreation"),
      type: "date",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      valueGetter: (data: Row, row: any) => new Date(row?.creationInfos.date),
      renderCell: (data: Row) => (
        <TableCellOwner2Lines
          userName={data.row["createdByUserName"]}
          groupName={moment(data?.row?.creationInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
        />
      ),
    },
    {
      field: "labProviderName",
      headerName: t("table:headerName.labProvider"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => <Typography>{data.value || "-"}</Typography>,
    },
    {
      field: "objectId",
      headerName: t("common:assetId"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: Row) => (
        <Link underline="hover" to={`${routePaths.asset.path}/${data.value}`} component={RouterLink} target="_blank">
          {data.value}
        </Link>
      ),
    },
    {
      field: "asset",
      headerName: t("common:assetTypeUpperCase"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      valueGetter: (data: any, row: any) => row?.asset.type + "-" + row?.asset.name + "-" + row?.asset.comment,
      renderCell: (data: Row) => (
        <TableCellAsset
          data={{
            row: {
              type: data?.row?.asset.type,
              desc: data?.row?.asset.comment,
              title: data?.row?.asset.name,
              typeHide: false,
              onlyPicto: true,
            },
          }}
        />
      ),
    },
    {
      field: "status",
      headerName: t("common:status"),
      type: "singleSelect",
      valueOptions: Object.keys(JobStatus),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => <TableCellJobStatus data={{ ...data, value: data?.row?.status }} />,
    },
    {
      field: "annotation",
      headerName: t("common:message"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <Stack sx={{ flexWrap: "wrap", wordWrap: "break-word", gap: 0.5, my: 1 }}>{data.value}</Stack>
      ),
    },
    {
      field: "progress",
      headerName: t("common:progress"),
      type: "number",
      headerAlign: "left",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => (
        <TableCellWorkflowProgressBar progress={data.value} status={GetRowValueFromStatus(data?.row?.status)} />
      ),
    },
    {
      field: "actions",
      headerName: t("common:action"),
      type: "actions",
      flex: CellSize.actionsSmall,
      minWidth: CellSize.actionsSmall,
      getActions: (params: GridRowParams) =>
        !params || !params.row
          ? []
          : params.row["status"] === WorkflowStatus.ABORTED ||
            params.row["status"] === WorkflowStatus.CANCELLED ||
            params.row["status"] === WorkflowStatus.FAILED
          ? [
              <Button onClick={() => onClickRetryWorkflow(params.row["id"])} size="small">
                <ReplayIcon />
              </Button>,
            ]
          : [
              <Button
                onClick={() => onConfirmPurge([params.row["id"]])}
                size="small"
                disabled={params?.row?.status !== WorkflowStatus.READY}
              >
                <PlayCircleOutlineOutlinedIcon />
              </Button>,
            ],
    },
  ];

  const openAssetPanel = (row) => {
    setAssetSelected(row.row.objectId);
    setRowSelected(row.row.id);
    if (!assetPanel) toggleAssetPanelOpen();
  };

  const additionalFilters = (
    <>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<SettingsApplicationsOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={statusFilter}
        onChange={(e) => setStatusFilter(WorkflowStatus[e.target.value as string] || "Default")}
      >
        <MenuItem value={"Default"}>{t("common:status")}</MenuItem>
        {Object.keys(WorkflowStatus).map((status, index) => (
          <MenuItem value={status} key={index}>
            {status}
          </MenuItem>
        ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<EqualizerOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={typeFilter}
        onChange={(e) => setTypeFilter(ObjectType[e.target.value as string] || "Default")}
      >
        <MenuItem value={"Default"}>{t("common:assetType")}</MenuItem>
        {Object.keys(ObjectType)
          .filter((x) => purgeRows?.find((r) => r.asset?.type === x))
          .map((type, index) => (
            <MenuItem value={type} key={index}>
              {type}
            </MenuItem>
          ))}
      </Select>
    </>
  );

  const additionalActions = (
    <>
      {selectedPurge.length > 0 && (
        <Button variant="contained" color="primary" onClick={() => onConfirmPurge(selectedPurge)}>
          {t("button:purgeAll")}
        </Button>
      )}
    </>
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.DASHBOARD_PURGES_TABLE}
        additionalFilters={additionalFilters}
        additionalActions={additionalActions}
        rows={purgeRows.filter(
          (row) =>
            (statusFilter !== "Default" ? row.status === statusFilter : true) &&
            (typeFilter !== "Default" ? row.asset.type === typeFilter : true)
        )}
        columns={purgeColumns}
        getRowHeight={() => "auto"}
        columnHeaderHeight={48}
        checkboxSelection
        onRowClick={(e) => openAssetPanel(e)}
        onRowSelectionModelChange={handleSelection}
        initialState={{
          sorting: { sortModel: [{ field: "startTime", sort: "desc" }] },
        }}
        isRowSelectable={(params: GridRowParams) =>
          params?.row?.status !== WorkflowStatus.COMPLETED && params?.row?.status !== WorkflowStatus.RUNNING
        }
        initialColumnVisibility={{
          actions: UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser], AclMatch.OneAtLeast),
        }}
      />
      <AssetPanel
        open={assetPanel}
        onClose={toggleAssetPanelOpen}
        closePanel={toggleAssetPanelOpen}
        assetId={assetSelected}
        reload={rowSelected}
      />
    </>
  );
}

export default DashboardPurge;
